import type { MouseEvent, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type PillTone = 'neutral' | 'warning' | 'success' | 'danger' | 'coral' | 'teal';
export type PillSize = 'sm' | 'md';

export interface PillProps {
  tone?: PillTone;
  size?: PillSize;
  icon?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** When provided, the pill renders as a button and fires this on click. */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /** Accessible label, used when the pill is interactive. */
  ariaLabel?: string;
  /** Native tooltip — lets a truncated pill reveal its full text on hover. */
  title?: string;
}

const TONE_STYLES: Record<PillTone, string> = {
  neutral: 'bg-warm-bg text-muted-foreground border-border',
  warning: 'bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))] border-[hsl(var(--status-warning)/0.3)]',
  success: 'bg-teal-light text-teal-dark border-teal/30',
  danger: 'bg-[hsl(var(--status-error-bg))] text-[hsl(var(--status-error))] border-[hsl(var(--status-error)/0.3)]',
  coral: 'bg-coral-light text-coral-dark border-coral/25',
  teal: 'bg-teal-light text-teal-dark border-teal/30',
};

const SIZE_STYLES: Record<PillSize, string> = {
  sm: 'px-1.5 py-0.5 text-[11px] gap-1',
  md: 'px-2.5 py-1 text-[12px] gap-1.5',
};

export function Pill({
  tone = 'neutral',
  size = 'sm',
  icon,
  children,
  className,
  onClick,
  ariaLabel,
  title,
}: PillProps) {
  const baseClass = cn(
    'inline-flex items-center font-medium border rounded',
    TONE_STYLES[tone],
    SIZE_STYLES[size],
    className,
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        title={title}
        className={cn(baseClass, 'cursor-pointer hover:opacity-90 transition-opacity')}
      >
        {icon && <span className="inline-flex shrink-0">{icon}</span>}
        {children}
      </button>
    );
  }

  return (
    <span className={baseClass} title={title}>
      {icon && <span className="inline-flex shrink-0">{icon}</span>}
      {children}
    </span>
  );
}
